import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Home as HomeIcon,
  Campaign as CampaignIcon,
  Help as HelpIcon,
  AdminPanelSettings as AdminIcon,
  Mic as MicIcon,
  Favorite as FavoriteIcon,
  Description as DescriptionIcon,
  Inbox as InboxIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon2,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarToday as CalendarIcon,
  Info as InfoIcon2,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  VpnKey as VpnKeyIcon,
  WorkspacePremium as WorkspacePremiumIcon,
  Devices as DevicesIcon,
  QrCode2 as QrCodeIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import { ResponsiveAdminLayout } from '../components/ResponsiveAdminLayout';
import { FreeTrialUpgradeCard } from '../components/FreeTrialUpgradeCard';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { useAuth } from '../hooks/useAuth';
import { useCompany } from '../hooks/useAuth';
import { AppColors } from '../theme/colors';
import { doc, getDoc, collection, query, where, getDocs, onSnapshot, orderBy, limit, startAfter } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { AuthService } from '../services/authService';
import {
  StripeService,
  BankTransferInstructionsResponse,
  formatBankAccountType,
} from '../services/stripeService';
import { buildFunctionUrl } from '../utils/functionsUrl';
import { buildAuthHeaders } from '../utils/authHeaders';

interface CompanyData {
  id: string;
  companyName: string;
  corporateCode: string;
  contactPerson: string;
  contactEmail: string;
  postalCode?: string;
  address?: string;
  phoneNumber?: string;
  currentPlan: string;
  maxDevices: number;
  registeredDevices: number;
  createdAt: Date;
  rotatedAt?: Date;
  rotateReason?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  paymentMethod?: 'card' | 'bank_transfer';
  serviceStatus?: string;
  isFreeTrial?: boolean;
  freeTrialEndDate?: any;
  trialEndDate?: any;
  // 予約関連
  pendingPlan?: string;
  pendingMaxDevices?: number;
  planChangeEffectiveAt?: any;
  planChangeType?: 'upgrade' | 'downgrade';
  planChangeStatus?: 'scheduled' | 'applied' | 'cancelled';
}

interface Invoice {
  invoiceId: string;
  hostedInvoiceUrl: string;
  invoicePdf: string;
  amountDue: number;
  currency: string;
  status: string;
  dueDate: Date;
  createdAt: Date;
  number?: string;
  periodStart?: Date;
  periodEnd?: Date;
}

const getTaxIncludedPrice = (price: string): string => {
  const priceMap: { [key: string]: string } = {
    '¥12,000/月(税抜)': '(税込¥13,200)',
    '¥15,000/月(税抜)': '(税込¥16,500)',
    '¥24,000/月(税抜)': '(税込¥26,400)',
    '¥30,000/月(税抜)': '(税込¥33,000)',
    '¥33,000/月(税抜)': '(税込¥36,300)',
    '¥36,000/月(税抜)': '(税込¥39,600)',
  };
  return priceMap[price] || '';
};

const getMaxDevicesForPlan = (planName: string): number => {
  const planLimits: { [key: string]: number } = {
    'プラン50': 50,
    'プラン100': 100,
    'プラン200': 200,
    'プラン300': 300,
    'プラン400': 400,
    'プラン500': 500,
  };
  return planLimits[planName] || 100;
};

export const CompanyManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { company, loading: companyLoading } = useCompany(user?.uid || null);
  const isDesktop = useIsDesktop();
  
  const [isSystemAdminExpanded, setIsSystemAdminExpanded] = useState(false);
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [loading, setLoading] = useState(false);
  
  // 編集状態
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  
  // フォーム状態
  
  // ダイアログ状態

  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDialogValue, setEmailDialogValue] = useState('');
  const [emailDialogLoading, setEmailDialogLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>(companyData?.currentPlan || '');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [basicInfoDialogOpen, setBasicInfoDialogOpen] = useState(false);
  const [basicInfoDialogLoading, setBasicInfoDialogLoading] = useState(false);
  const [basicInfoForm, setBasicInfoForm] = useState({
    companyName: '',
    contactPerson: '',
    postalCode: '',
    address: '',
    phoneNumber: '',
  });
  const [basicInfoErrors, setBasicInfoErrors] = useState<{ [key: string]: string }>({});
  
  // 請求書と振込先情報の状態
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [bankInstructions, setBankInstructions] = useState<BankTransferInstructionsResponse | null>(null);
  const [loadingBankInstructions, setLoadingBankInstructions] = useState(false);
  // カード情報の状態
  const [paymentMethod, setPaymentMethod] = useState<{
    brand: string | null;
    last4: string | null;
    expMonth: number | null;
    expYear: number | null;
  } | null>(null);
  const [loadingPaymentMethod, setLoadingPaymentMethod] = useState(false);
  // 請求書ページネーション用
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [hasMoreInvoices, setHasMoreInvoices] = useState(true);
  // 番号ページネーション用
  const PAGE_SIZE = 2; // テスト用。必要に応じて変更
  const [pages, setPages] = useState<Invoice[][]>([]); // 読み込んだページのキャッシュ
  const [pageCursors, setPageCursors] = useState<any[]>([]); // 各ページの最後のドキュメント
  const [currentPage, setCurrentPage] = useState(1);
  // 未払い（open）のinvoiceの数を管理
  const [unpaidInvoiceCount, setUnpaidInvoiceCount] = useState<number>(0);

  // 月別グルーピング（表示用）- periodStartベースでグループ化
  const groupedInvoices = useMemo(() => {
    const groups: { key: string; label: string; items: Invoice[] }[] = [];
    const bucket = new Map<string, Invoice[]>();

    for (const inv of invoices) {
      // Stripe上ではvoid請求書も履歴として残るが、
      // 管理画面では「請求対象外」のため一覧表示からは除外する。
      if (inv.status === 'void') {
        continue;
      }
      // periodStartがあればそれを使用、なければcreatedAtにフォールバック
      const baseDate = inv.periodStart || inv.createdAt;
      const d = baseDate instanceof Date ? baseDate : new Date(baseDate);
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!bucket.has(ym)) bucket.set(ym, []);
      bucket.get(ym)!.push(inv);
    }

    // 降順（新しい月が上）
    const sortedKeys = Array.from(bucket.keys()).sort((a, b) => (a < b ? 1 : -1));
    for (const key of sortedKeys) {
      const [y, m] = key.split('-');
      groups.push({
        key,
        label: `${y}年${Number(m)}月`,
        items: bucket.get(key)!.sort((a, b) => {
          const aDate = a.periodStart || a.createdAt;
          const bDate = b.periodStart || b.createdAt;
          return aDate < bDate ? 1 : -1;
        }),
      });
    }

    return groups;
  }, [invoices]);

  // フィルタ後に実際に画面に表示される請求書の枚数
  const visibleInvoiceCount = useMemo(() => {
    return groupedInvoices.reduce((acc, group) => acc + group.items.length, 0);
  }, [groupedInvoices]);

  // トライアル（¥0）請求書が含まれるか
  const hasTrialInvoices = useMemo(() => invoices.some((inv) => inv.amountDue === 0), [invoices]);

  // 未払い（open）のinvoiceがあるかどうか
  const hasUnpaidInvoices = useMemo(() => unpaidInvoiceCount > 0, [unpaidInvoiceCount]);

  // 請求書ドキュメント -> UIモデル 変換
  const mapInvoice = (docSnap: any): Invoice => {
    const data = docSnap.data();
    return {
      invoiceId: data.invoiceId || docSnap.id,
      hostedInvoiceUrl: data.hostedInvoiceUrl || '',
      invoicePdf: data.invoicePdf || '',
      amountDue: data.amountDue || 0,
      currency: data.currency || 'jpy',
      status: data.status || 'open',
      dueDate: data.dueDate?.toDate ? data.dueDate.toDate() : (data.dueDate ? new Date(data.dueDate) : new Date()),
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : new Date()),
      number: data.number || '',
      periodStart: data.periodStart?.toDate ? data.periodStart.toDate() : (data.periodStart ? new Date(data.periodStart) : undefined),
      periodEnd: data.periodEnd?.toDate ? data.periodEnd.toDate() : (data.periodEnd ? new Date(data.periodEnd) : undefined),
    };
  };

  useEffect(() => {
    if (company?.id) {
      fetchCompanyData();
    }
  }, [company?.id]);

  // 初回ページ読み込み
  const loadInitialInvoices = async () => {
    if (!company?.id || invoicesLoading) return;
    setInvoicesLoading(true);
    try {
      const invoicesRef = collection(db, 'companies', company.id, 'invoices');
      const q = query(invoicesRef, orderBy('createdAt', 'desc'), limit(PAGE_SIZE));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(mapInvoice);
      setInvoices(list); // 表示中ページ
      setPages([list]);
      const lastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
      setPageCursors([lastDoc]);
      setCurrentPage(1);
      
      // 次のページが存在するか確認（1件だけ取得してみる）
      if (lastDoc && snapshot.size === PAGE_SIZE) {
        const nextPageQuery = query(invoicesRef, orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(1));
        const nextPageSnapshot = await getDocs(nextPageQuery);
        setHasMoreInvoices(nextPageSnapshot.size > 0);
      } else {
        setHasMoreInvoices(false);
      }
    } catch (e) {
      console.error('[CompanyManagementPage] loadInitialInvoices error:', e);
      setInvoices([]);
      setHasMoreInvoices(false);
    } finally {
      setInvoicesLoading(false);
    }
  };

  // 任意ページ読み込み（1始まり）。未読ならカーソルを使って順次取得
  const loadPage = async (pageNum: number) => {
    if (!company?.id || invoicesLoading) return;
    // 既に読み込んでいればキャッシュから表示
    if (pageNum <= pages.length && pages[pageNum - 1]) {
      setInvoices(pages[pageNum - 1]);
      setCurrentPage(pageNum);
      // キャッシュから表示する場合も、hasMoreInvoicesを適切に設定
      if (pageNum === pages.length) {
        // 現在のページが最後の読み込み済みページの場合、次のページが存在するか確認
        const currentPageData = pages[pageNum - 1];
        if (currentPageData.length < PAGE_SIZE) {
          // データ数がPAGE_SIZE未満なら、次のページは存在しない
          setHasMoreInvoices(false);
        } else {
          // データ数がPAGE_SIZEと等しい場合、次のページが存在するか確認
          const lastCursor = pageCursors[pageNum - 1];
          if (lastCursor) {
            // 次のページを1件だけ取得してみる
            const invoicesRef = collection(db, 'companies', company.id, 'invoices');
            const nextPageQuery = query(invoicesRef, orderBy('createdAt', 'desc'), startAfter(lastCursor), limit(1));
            getDocs(nextPageQuery).then((nextPageSnapshot) => {
              setHasMoreInvoices(nextPageSnapshot.size > 0);
            }).catch(() => {
              setHasMoreInvoices(false);
            });
          } else {
            setHasMoreInvoices(false);
          }
        }
      } else {
        // 次のページが既に読み込まれている場合は、次のページが存在する
        setHasMoreInvoices(true);
      }
      return;
    }
    // 読み込みが必要
    setInvoicesLoading(true);
    try {
      const invoicesRef = collection(db, 'companies', company.id, 'invoices');
      const prevCursor = pageCursors[pageNum - 2];
      const q = prevCursor
        ? query(invoicesRef, orderBy('createdAt', 'desc'), startAfter(prevCursor), limit(PAGE_SIZE))
        : query(invoicesRef, orderBy('createdAt', 'desc'), limit(PAGE_SIZE));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(mapInvoice);
      const newPages = [...pages];
      newPages[pageNum - 1] = list;
      setPages(newPages);
      const newCursors = [...pageCursors];
      const lastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
      newCursors[pageNum - 1] = lastDoc;
      setPageCursors(newCursors);
      setInvoices(list);
      setCurrentPage(pageNum);
      
      // 次のページが存在するか確認（1件だけ取得してみる）
      if (lastDoc && snapshot.size === PAGE_SIZE) {
        const nextPageQuery = query(invoicesRef, orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(1));
        const nextPageSnapshot = await getDocs(nextPageQuery);
        setHasMoreInvoices(nextPageSnapshot.size > 0);
      } else {
        setHasMoreInvoices(false);
      }
    } catch (e) {
      console.error('[CompanyManagementPage] loadPage error:', e);
    } finally {
      setInvoicesLoading(false);
    }
  };

  // 表示を初期状態（最新1件）に戻す
  const resetInvoicesToInitial = async () => {
    setInvoices([]);
    setPages([]);
    setPageCursors([]);
    setHasMoreInvoices(true);
    await loadInitialInvoices();
  };

  // company 取得時に会社情報と請求書の初回読み込み
  useEffect(() => {
    if (company?.id) {
      fetchCompanyData();
      loadInitialInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  // カード更新後のリダイレクト処理
  useEffect(() => {
    const cardUpdated = searchParams.get('card_updated');
    const cardUpdateCancelled = searchParams.get('card_update_cancelled');
    
    if (cardUpdated === 'true') {
      setSnackbar({
        open: true,
        message: 'カード情報の更新が完了しました',
        severity: 'success',
      });
      // URLパラメータを削除
      setSearchParams({}, { replace: true });
      // カード情報を再取得
      if (companyData?.stripeCustomerId && companyData?.paymentMethod === 'card') {
        const fetchPaymentMethod = async () => {
          setLoadingPaymentMethod(true);
          try {
            const pm = await StripeService.getPaymentMethod(companyData.stripeCustomerId!);
            setPaymentMethod(pm);
          } catch (error) {
            console.error('Failed to fetch payment method:', error);
          } finally {
            setLoadingPaymentMethod(false);
          }
        };
        fetchPaymentMethod();
      }
    } else if (cardUpdateCancelled === 'true') {
      setSnackbar({
        open: true,
        message: 'カード情報の更新がキャンセルされました',
        severity: 'error',
      });
      // URLパラメータを削除
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, companyData?.stripeCustomerId, companyData?.paymentMethod, setSearchParams]);

  // 振込先情報の取得
  useEffect(() => {
    if (!companyData?.stripeCustomerId || companyData?.paymentMethod !== 'bank_transfer') return;

    const fetchBankInstructions = async () => {
      setLoadingBankInstructions(true);
      try {
        const instructions = await StripeService.getBankTransferInstructions(companyData.stripeCustomerId!);
        setBankInstructions(instructions);
      } catch (error: any) {
        console.error('Failed to fetch bank instructions:', error);
        // 顧客が見つからない場合はエラーを表示しない（環境不一致の可能性）
        if (error.message?.includes('顧客情報が見つかりません')) {
          setBankInstructions(null);
        }
      } finally {
        setLoadingBankInstructions(false);
      }
    };

    fetchBankInstructions();
  }, [companyData?.stripeCustomerId, companyData?.paymentMethod]);

  // カード情報取得
  useEffect(() => {
    if (!companyData?.stripeCustomerId || companyData?.paymentMethod !== 'card') return;

    const fetchPaymentMethod = async () => {
      setLoadingPaymentMethod(true);
      try {
        const pm = await StripeService.getPaymentMethod(companyData.stripeCustomerId!);
        setPaymentMethod(pm);
      } catch (error: any) {
        console.error('Failed to fetch payment method:', error);
        // 顧客が見つからない場合はエラーを表示しない（環境不一致の可能性）
        if (error.message?.includes('顧客情報が見つかりません')) {
          setPaymentMethod(null);
        } else {
          setPaymentMethod(null);
        }
      } finally {
        setLoadingPaymentMethod(false);
      }
    };

    fetchPaymentMethod();
  }, [companyData?.stripeCustomerId, companyData?.paymentMethod]);

  // 未払い（open）のinvoiceの数を取得
  useEffect(() => {
    if (!company?.id) {
      setUnpaidInvoiceCount(0);
      return;
    }

    const invoicesRef = collection(db, 'companies', company.id, 'invoices');
    const q = query(invoicesRef, where('status', '==', 'open'));

    // リアルタイムで未払いのinvoiceの数を監視
    const unsubscribe = onSnapshot(q, (snapshot) => {
      // 金額が0円（トライアル）のinvoiceは除外
      const unpaidCount = snapshot.docs.filter((doc) => {
        const data = doc.data();
        return (data.amountDue || 0) > 0;
      }).length;
      setUnpaidInvoiceCount(unpaidCount);
    }, (error) => {
      console.error('[CompanyManagementPage] Error fetching unpaid invoices:', error);
      setUnpaidInvoiceCount(0);
    });

    return () => unsubscribe();
  }, [company?.id]);

  // 表示対象の請求書が0枚で、かつ次ページがある場合は自動で次ページを読み込む
  useEffect(() => {
    if (invoicesLoading) return;
    // invoices 自体は存在するが、フィルタ後に1件も表示されないケースを補正する
    if (invoices.length > 0 && visibleInvoiceCount === 0 && hasMoreInvoices) {
      loadPage(currentPage + 1);
    }
  }, [invoicesLoading, invoices.length, visibleInvoiceCount, hasMoreInvoices, currentPage]);

  // 表示されている請求書のIDリスト（依存配列の最適化用）
  const invoiceIdsString = useMemo(() => {
    return invoices.map((inv) => inv.invoiceId).sort().join(',');
  }, [invoices.map((inv) => inv.invoiceId).join(',')]);

  // 表示されている請求書をリアルタイム監視してステータスを更新
  useEffect(() => {
    if (!company?.id || invoices.length === 0) return;

    // 各請求書に対してリアルタイム監視を設定
    const unsubscribeFunctions: (() => void)[] = [];

    invoices.forEach((invoice) => {
      const invoiceRef = doc(db, 'companies', company.id, 'invoices', invoice.invoiceId);
      const unsubscribe = onSnapshot(invoiceRef, (docSnapshot) => {
        if (!docSnapshot.exists()) return;

        const data = docSnapshot.data();
        const updatedInvoice: Invoice = {
          invoiceId: invoice.invoiceId,
          hostedInvoiceUrl: data.hostedInvoiceUrl || invoice.hostedInvoiceUrl,
          invoicePdf: data.invoicePdf || invoice.invoicePdf,
          amountDue: data.amountDue || 0,
          currency: data.currency || invoice.currency,
          status: data.status || invoice.status,
          dueDate: data.dueDate?.toDate ? data.dueDate.toDate() : (data.dueDate ? new Date(data.dueDate) : invoice.dueDate),
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : invoice.createdAt),
          number: data.number || invoice.number,
        };

        // 該当する請求書の状態を更新
        setInvoices((prevInvoices) => {
          return prevInvoices.map((inv) => 
            inv.invoiceId === invoice.invoiceId ? updatedInvoice : inv
          );
        });

        // ページキャッシュも更新
        setPages((prevPages) => {
          return prevPages.map((page) => 
            page.map((inv) => 
              inv.invoiceId === invoice.invoiceId ? updatedInvoice : inv
            )
          );
        });
      }, (error) => {
        console.error(`[CompanyManagementPage] Error watching invoice ${invoice.invoiceId}:`, error);
      });

      unsubscribeFunctions.push(unsubscribe);
    });

    // クリーンアップ関数
    return () => {
      unsubscribeFunctions.forEach((unsubscribe) => unsubscribe());
    };
  }, [company?.id, invoiceIdsString]);

  // カード変更処理
  const handleUpdateCard = async () => {
    if (!companyData?.stripeCustomerId || !company?.id) return;
    
    try {
      const url = await StripeService.createCardUpdateSession(
        companyData.stripeCustomerId,
        company.id
      );
      window.location.href = url;
    } catch (error: any) {
      console.error('Failed to create card update session:', error);
      setSnackbar({
        open: true,
        message: error.message || 'カード変更セッションの作成に失敗しました',
        severity: 'error',
      });
    }
  };

  const fetchCompanyData = async () => {
    if (!company?.id) return;
    
    setLoading(true);
    try {
      // Firestoreから会社データを取得
      const companyRef = doc(db, 'companies', company.id);
      const companyDoc = await getDoc(companyRef);
      
      if (companyDoc.exists()) {
        const data = companyDoc.data();

        const companyData: CompanyData = {
          id: company.id,
          companyName: data.companyName || '',
          corporateCode: data.corporateCode || '',
          contactPerson: data.contactPerson || '',
          contactEmail: data.contactEmail || '',
          postalCode: data.postalCode || '',
          address: data.address || '',
          phoneNumber: data.phoneNumber || '',
          currentPlan: data.currentPlan || 'プラン50',
          maxDevices: data.maxDevices || 50,
          registeredDevices: data.registeredDevices || 0,
          createdAt: data.createdAt?.toDate() || new Date(),
          rotatedAt: data.rotatedAt?.toDate(),
          rotateReason: data.rotateReason,
          stripeCustomerId: data.stripeCustomerId || '',
          stripeSubscriptionId: data.stripeSubscriptionId || undefined,
          paymentMethod: data.paymentMethod || undefined,
          serviceStatus: data.serviceStatus,
          isFreeTrial: data.isFreeTrial === true,
          freeTrialEndDate: data.freeTrialEndDate,
          trialEndDate: data.trialEndDate,
          // 予約情報を反映
          pendingPlan: data.pendingPlan,
          pendingMaxDevices: data.pendingMaxDevices,
          planChangeEffectiveAt: data.planChangeEffectiveAt,
          planChangeType: data.planChangeType,
          planChangeStatus: data.planChangeStatus,
        };
        
        setCompanyData(companyData);
      }
    } catch (error) {
      console.error('会社データ取得エラー:', error);
      setSnackbar({
        open: true,
        message: '会社データの取得に失敗しました',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      navigate('/admin-login');
    } catch (error) {
      console.error('ログアウトエラー:', error);
    }
  };

  const handleEmailEdit = () => {
    setEmailDialogValue(companyData?.contactEmail || '');
    setEmailDialogOpen(true);
  };

  const handleBasicInfoEdit = () => {
    if (!companyData) return;
    setBasicInfoForm({
      companyName: companyData.companyName || '',
      contactPerson: companyData.contactPerson || '',
      postalCode: companyData.postalCode || '',
      address: companyData.address || '',
      phoneNumber: companyData.phoneNumber || '',
    });
    setBasicInfoErrors({});
    setBasicInfoDialogOpen(true);
  };

  // 郵便番号のフォーマット処理（3桁入力したらハイフンを自動挿入）
  const formatPostalCode = (value: string): string => {
    // 数字以外の文字を削除（ハイフンは除く）
    const numbers = value.replace(/[^\d-]/g, '');
    
    // ハイフンが既にある場合は一旦削除してから処理
    const digitsOnly = numbers.replace(/-/g, '');
    
    // 7桁まで入力可能
    if (digitsOnly.length <= 3) {
      return digitsOnly;
    } else if (digitsOnly.length <= 7) {
      // 3桁目以降はハイフンを挿入
      return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;
    } else {
      // 7桁を超える場合は切り詰め
      return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 7)}`;
    }
  };

  const handleBasicInfoChange = (field: keyof typeof basicInfoForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    
    // 郵便番号の場合はフォーマット処理を適用
    if (field === 'postalCode') {
      value = formatPostalCode(value);
    }
    
    setBasicInfoForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateBasicInfo = () => {
    const errors: { [key: string]: string } = {};
    
    // 会社名（必須）
    if (!basicInfoForm.companyName.trim()) {
      errors.companyName = '会社名を入力してください';
    }
    
    // 担当者名（必須）
    if (!basicInfoForm.contactPerson.trim()) {
      errors.contactPerson = '担当者名を入力してください';
    }
    
    // 郵便番号（必須）
    if (!basicInfoForm.postalCode.trim()) {
      errors.postalCode = '郵便番号を入力してください';
    } else {
      const postalCodeRegex = /^([0-9]{3}-?[0-9]{4}|[0-9]{7})$/;
      if (!postalCodeRegex.test(basicInfoForm.postalCode.trim())) {
        errors.postalCode = '郵便番号の形式が正しくありません';
      }
    }
    
    // 住所（必須）
    if (!basicInfoForm.address.trim()) {
      errors.address = '住所を入力してください';
    }
    
    // 電話番号（必須）
    if (!basicInfoForm.phoneNumber.trim()) {
      errors.phoneNumber = '電話番号を入力してください';
    } else {
      const phoneRegex = /^[-0-9()+\s]{5,20}$/;
      if (!phoneRegex.test(basicInfoForm.phoneNumber.trim())) {
        errors.phoneNumber = '電話番号の形式が正しくありません';
      }
    }
    
    setBasicInfoErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBasicInfoDialogClose = () => {
    if (basicInfoDialogLoading) return;
    setBasicInfoDialogOpen(false);
    setBasicInfoErrors({});
  };

  const handleBasicInfoSave = async () => {
    if (!companyData?.id) return;
    if (!validateBasicInfo()) return;

    setBasicInfoDialogLoading(true);
    try {
      if (!user?.uid) {
        throw new Error('ユーザー情報が取得できません');
      }

      const payload = {
        companyId: companyData.id,
        companyName: basicInfoForm.companyName.trim(),
        contactPerson: basicInfoForm.contactPerson.trim(),
        postalCode: basicInfoForm.postalCode.trim(),
        address: basicInfoForm.address.trim(),
        phoneNumber: basicInfoForm.phoneNumber.trim(),
      };

      const response = await fetch(
        buildFunctionUrl('updateCompanyBasicInfo'),
        {
          method: 'POST',
          headers: await buildAuthHeaders(),
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || '基本情報の更新に失敗しました');
      }

      await fetchCompanyData();

      setSnackbar({
        open: true,
        message: '基本情報を更新しました',
        severity: 'success',
      });
      setBasicInfoDialogOpen(false);
    } catch (error: any) {
      console.error('[CompanyManagementPage] 基本情報更新エラー:', error);
      setSnackbar({
        open: true,
        message: error?.message || '基本情報の更新に失敗しました',
        severity: 'error',
      });
    } finally {
      setBasicInfoDialogLoading(false);
    }
  };

  const handleEmailDialogClose = () => {
    setEmailDialogOpen(false);
    setEmailDialogValue('');
  };

  const handleEmailDialogSave = async () => {
    if (!emailDialogValue.trim() || !emailDialogValue.includes('@')) {
      setSnackbar({
        open: true,
        message: '有効なメールアドレスを入力してください',
        severity: 'error',
      });
      return;
    }

    setEmailDialogLoading(true);
    try {
      if (!company?.id) {
        throw new Error('会社IDが取得できません');
      }

      if (!user?.uid) {
        throw new Error('ユーザー情報が取得できません');
      }

      // Cloud Functionsを呼び出してメールアドレスを更新
      const response = await fetch(
        buildFunctionUrl('updateCompanyResponsibleEmail'),
        {
          method: 'POST',
          headers: await buildAuthHeaders(),
          body: JSON.stringify({
            companyId: company.id,
            newEmail: emailDialogValue.trim(),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'メールアドレスの更新に失敗しました');
      }
      
      setSnackbar({
        open: true,
        message: 'メールアドレスを更新しました',
        severity: 'success',
      });
      
      setEmailDialogOpen(false);
      await fetchCompanyData();
    } catch (error: any) {
      console.error('メールアドレス更新エラー:', error);
      setSnackbar({
        open: true,
        message: error.message || 'メールアドレスの更新に失敗しました',
        severity: 'error',
      });
    } finally {
      setEmailDialogLoading(false);
    }
  };

  const handlePlanEdit = () => {
    setSelectedPlan(companyData?.currentPlan || '');
    setPlanDialogOpen(true);
  };

  const handlePlanSave = async (newPlan: string) => {
    setIsLoadingPlan(true);
    try {
      if (!company?.id) {
        throw new Error('会社情報が取得できませんでした');
      }
      // Cloud Functions 経由で「翌月1日適用」の予約を作成
      try {
        if (!user?.uid) {
          throw new Error('ユーザー情報が取得できません');
        }
        
        // デバイス情報を取得
        const { coarse, fine } = await AuthService.generateFingerprints();
        const corporateCode = company.corporateCode || company.id;
        const physicalDeviceId = `${corporateCode}_${coarse}`;
        const deviceId = `${corporateCode}_${fine}`;
        
        const { key: normalizedPlanName, priceId: stripePriceId } = StripeService.resolvePriceId(newPlan);
        if (!stripePriceId) {
          throw new Error('選択したプランに対応する価格IDが設定されていません');
        }
        const response = await fetch(buildFunctionUrl('scheduleCompanyPlanChange'), {
          method: 'POST',
          headers: await buildAuthHeaders(),
          body: JSON.stringify({
            companyId: company.id,
            newPlan: normalizedPlanName, // 正規化されたプラン名を送信
            stripePriceId,
            deviceInfo: {
              deviceId,
              physicalDeviceId,
              coarseFingerprint: coarse,
              fineFingerprint: fine,
            },
          }),
        });
        
        if (!response.ok) {
          throw new Error('プラン変更の予約に失敗しました');
        }
        
        const data = await response.json();
        const effectiveDate = data.effectiveAt ? new Date(data.effectiveAt).toLocaleDateString('ja-JP') : '翌月1日';
        
        setSnackbar({
          open: true,
          message: `プラン変更を予約しました（適用日: ${effectiveDate}）`,
          severity: 'success',
        });
      } catch (e) {
        console.error('プラン変更予約エラー:', e);
        setSnackbar({
          open: true,
          message: 'プラン変更の予約に失敗しました',
          severity: 'error',
        });
      }

      setPlanDialogOpen(false);
      await fetchCompanyData();
    } catch (error) {
      console.error('プラン更新エラー:', error);
      setSnackbar({
        open: true,
        message: 'プラン変更の処理に失敗しました',
        severity: 'error',
      });
    } finally {
      setIsLoadingPlan(false);
    }
  };

  // 予約キャンセル（同一プランで予約関数を呼び、pendingをクリア）
  const handleCancelScheduledChange = async () => {
    try {
      if (!company?.id || !companyData?.currentPlan) return;
      if (!user?.uid) {
        throw new Error('ユーザー情報が取得できません');
      }
      const resp = await fetch(buildFunctionUrl('scheduleCompanyPlanChange'), {
        method: 'POST',
        headers: await buildAuthHeaders(),
        body: JSON.stringify({
          companyId: company.id,
          newPlan: companyData.currentPlan,
        }),
      });
      if (!resp.ok) {
        try {
          const err = await resp.json();
          throw new Error(err?.error || '予約キャンセルAPIエラー');
        } catch (_) {
          throw new Error('予約キャンセルAPIエラー');
        }
      }
      setSnackbar({
        open: true,
        message: 'プラン変更の予約をキャンセルしました',
        severity: 'success',
      });
      // 即時にUIを反映（Firestoreの整合前でも非表示にする）
      setCompanyData(prev => prev ? {
        ...prev,
        pendingPlan: undefined,
        pendingMaxDevices: undefined,
        planChangeEffectiveAt: undefined as any,
        planChangeStatus: undefined,
        planChangeType: undefined,
      } : prev);
      // 念のため再取得
      await fetchCompanyData();
    } catch (e: any) {
      console.error('プラン変更予約キャンセルエラー:', e);
      setSnackbar({
        open: true,
        message: e?.message || 'プラン変更の予約キャンセルに失敗しました',
        severity: 'error',
      });
    }
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({
      open: true,
      message: 'クリップボードにコピーしました',
      severity: 'success',
    });
  };

  // QRコード・URLは専用の画面（メニューの「QRコード・URL」）に移した（坂本様ご指摘④）。
  // 会社管理で探す方もいるため、ボタンは残して専用の画面へ案内する
  const handleQrCodeClick = () => {
    navigate('/admin/access-link');
  };

  // 無料期間から切り替えた直後は請求書がまだないので、いつから始まるかを添える。
  // trialEndDate は切り替えのときに Stripe が返した課金開始日。
  const billingStartLabel = (() => {
    const raw = companyData?.trialEndDate;
    const start = raw?.toDate?.() ?? (raw instanceof Date ? raw : null);
    if (!start || start.getTime() <= Date.now()) return null;
    return start.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' });
  })();

  // ヘッダーの無料期間から来たときは、切り替えのカードまで送る
  useEffect(() => {
    if (window.location.hash !== '#free-trial-upgrade' || !companyData) return;
    const timer = setTimeout(() => {
      document.getElementById('free-trial-upgrade')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [companyData]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  if (authLoading || companyLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!companyData) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <BusinessIcon sx={{ fontSize: 64, color: AppColors.textSecondary, mb: 2 }} />
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: AppColors.textSecondary, mb: 1 }}>
          会社情報が見つかりません
        </Typography>
        <Typography sx={{ fontSize: 14, color: AppColors.textSecondary }}>
          先に会社IDでログインしてください
        </Typography>
      </Box>
    );
  }

  // 無料実証の会社は、有料へ切り替えるときに郵便番号と住所が要る。
  // 未登録のうちは目立たせて、先に入れていただく。
  const needsAddressForUpgrade = Boolean(
    companyData?.isFreeTrial && !companyData?.stripeSubscriptionId
  );
  const showUpgradeCard = needsAddressForUpgrade;

  return (
    <ResponsiveAdminLayout pageTitle="会社管理">
      <Box>

        {/* 基準の余白の状態: PCは最大幅1000px・中央寄せ */}
        <Box sx={{ maxWidth: isDesktop ? '1000px' : '100%', mx: isDesktop ? 'auto' : 0, width: '100%' }}>
        

        {/* コンテンツエリア */}
        <Box sx={{ p: 2 }}>
        {/* 無料実証の会社にだけ、有料利用への切り替えを出す。
            停止中は先頭に置く。スクロールしないと見つからないため。 */}
        {showUpgradeCard && companyData.serviceStatus === 'suspended' && (
          <FreeTrialUpgradeCard
            companyId={companyData.id}
            freeTrialEndDate={companyData.freeTrialEndDate}
            postalCode={companyData.postalCode}
            address={companyData.address}
            isSuspended
          />
        )}

        {/* 基本情報セクション */}
        <Card
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: `1px solid ${AppColors.border}`,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: 'black',
              }}
            >
              基本情報
            </Typography>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              size="small"
              onClick={handleBasicInfoEdit}
            >
              編集
            </Button>
          </Box>

          {/* 会社名 */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <BusinessIcon sx={{ fontSize: 20, color: AppColors.textSecondary, mr: 1 }} />
              <Typography
                sx={{
                  fontSize: 14,
                  color: AppColors.textSecondary,
                }}
              >
                会社名
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                bgcolor: AppColors.surface,
                borderRadius: 2,
                border: `1px solid ${AppColors.border}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  color: 'black',
                }}
              >
                {companyData.companyName}
              </Typography>
            </Box>
          </Box>

          {/* 担当者名 */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PersonIcon sx={{ fontSize: 20, color: AppColors.textSecondary, mr: 1 }} />
              <Typography
                sx={{
                  fontSize: 14,
                  color: AppColors.textSecondary,
                }}
              >
                担当者名
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                bgcolor: AppColors.surface,
                borderRadius: 2,
                border: `1px solid ${AppColors.border}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  color: 'black',
                }}
              >
                {companyData.contactPerson || '未設定'}
              </Typography>
            </Box>
          </Box>

          {/* 郵便番号 */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LocationOnIcon sx={{ fontSize: 20, color: AppColors.textSecondary, mr: 1 }} />
              <Typography
                sx={{
                  fontSize: 14,
                  color: AppColors.textSecondary,
                }}
              >
                郵便番号
              </Typography>
              {needsAddressForUpgrade && !companyData.postalCode && (
                <Typography sx={{ fontSize: 13, color: AppColors.error, ml: 1 }}>
                  ※お支払いに必要です。入力してください
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                p: 1.5,
                bgcolor: AppColors.surface,
                borderRadius: 2,
                border: `1px solid ${AppColors.border}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  // 無料実証の会社は、お支払いに必要なため未登録を目立たせる
                  color: 'black',
                }}
              >
                {companyData.postalCode || ''}
              </Typography>
            </Box>
          </Box>

          {/* 住所 */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LocationOnIcon sx={{ fontSize: 20, color: AppColors.textSecondary, mr: 1 }} />
              <Typography
                sx={{
                  fontSize: 14,
                  color: AppColors.textSecondary,
                }}
              >
                住所
              </Typography>
              {needsAddressForUpgrade && !companyData.address && (
                <Typography sx={{ fontSize: 13, color: AppColors.error, ml: 1 }}>
                  ※お支払いに必要です。入力してください
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                p: 1.5,
                bgcolor: AppColors.surface,
                borderRadius: 2,
                border: `1px solid ${AppColors.border}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  color: 'black',
                }}
              >
                {companyData.address || '未設定'}
              </Typography>
            </Box>
          </Box>

          {/* 電話番号 */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PhoneIcon sx={{ fontSize: 20, color: AppColors.textSecondary, mr: 1 }} />
              <Typography
                sx={{
                  fontSize: 14,
                  color: AppColors.textSecondary,
                }}
              >
                電話番号
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                bgcolor: AppColors.surface,
                borderRadius: 2,
                border: `1px solid ${AppColors.border}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  color: 'black',
                }}
              >
                {companyData.phoneNumber || '未設定'}
              </Typography>
            </Box>
          </Box>
        </Card>

        {/* システム設定セクション */}
        <Card
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: `1px solid ${AppColors.border}`,
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: 'black',
              mb: 2.5,
            }}
          >
            システム設定
          </Typography>

          {/* システム管理者メールアドレス */}
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <EmailIcon sx={{ fontSize: 20, color: AppColors.textSecondary, mr: 1 }} />
              <Typography
                sx={{
                  fontSize: 14,
                  color: AppColors.textSecondary,
                }}
              >
                システム管理者メールアドレス
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button
                startIcon={<EditIcon />}
                onClick={handleEmailEdit}
                sx={{ color: AppColors.primary }}
              >
                変更
              </Button>
            </Box>
            
            <Box
              sx={{
                p: 1.5,
                bgcolor: AppColors.surface,
                borderRadius: 2,
                border: `1px solid ${AppColors.border}`,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  color: 'black',
                  flex: 1,
                }}
              >
                {companyData.contactEmail || '未設定'}
              </Typography>
              <IconButton
                onClick={() => handleCopyToClipboard(companyData.contactEmail)}
                sx={{ color: AppColors.primary }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* 会社ID */}
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <VpnKeyIcon sx={{ fontSize: 20, color: AppColors.textSecondary, mr: 1 }} />
              <Typography
                sx={{
                  fontSize: 14,
                  color: AppColors.textSecondary,
                }}
              >
                会社ID
              </Typography>
            </Box>
            
            <Box
              sx={{
                p: 1.5,
                bgcolor: AppColors.surface,
                borderRadius: 2,
                border: `1px solid ${AppColors.border}`,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  color: 'black',
                  flex: 1,
                }}
              >
                {companyData.corporateCode || '未設定'}
              </Typography>
              <IconButton
                onClick={() => handleCopyToClipboard(companyData.corporateCode)}
                sx={{ color: AppColors.primary }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* QRコード発行（ワンアクション行） */}
          <Box sx={{ mb: 2 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<QrCodeIcon />}
              onClick={handleQrCodeClick}
              sx={{
                py: 1.75,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${AppColors.primary} 0%, ${AppColors.primaryLight} 100%)`,
                color: 'white',
                '&:hover': {
                  background: `linear-gradient(135deg, ${AppColors.primaryDark} 0%, ${AppColors.primary} 100%)`,
                  color: 'white',
                },
              }}
            >
              投稿用のQRコード・URL
            </Button>
          </Box>
        </Card>

        {/*
          プラン設定セクション（複数プラン運用時のために保持）

          現在はプランが「プラン50」に固定されており、
          管理画面からのプラン変更機能は利用しないため UI からは非表示にしています。
          将来的に再びプランを複数化し、管理画面から変更・予約したくなった場合は、
          下記の Card ブロック全体を復活させてください。

        <Card ...>
          ...
        </Card>
        */}

        {/*
          デバイス利用状況セクション

          プランごとの端末上限と現在の利用台数を可視化するための UI でしたが、
          現在はプランが1種類のみであり、上限の概念を前面に出す必要性が低いため
          画面からは非表示としています。

          将来的に上限管理を厳密に行いたくなった場合は、
          下記の Card ブロックを復活させてください。

        <Card ...>
          ...
        </Card>
        */}

        {showUpgradeCard && companyData.serviceStatus !== 'suspended' && (
          <FreeTrialUpgradeCard
            companyId={companyData.id}
            freeTrialEndDate={companyData.freeTrialEndDate}
            postalCode={companyData.postalCode}
            address={companyData.address}
          />
        )}

        {/* 請求・支払状況 */}
        <Card
          id="invoice-history"
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: `1px solid ${AppColors.border}`,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: 'black',
              }}
            >
              請求・支払状況
            </Typography>
          </Box>

          {invoices.length === 0 ? (
            <Box
              sx={{
                p: 3,
                bgcolor: AppColors.surface,
                borderRadius: 2,
                border: `1px solid ${AppColors.border}`,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontSize: 14, color: AppColors.textSecondary }}>
                請求書はまだありません
              </Typography>
              {billingStartLabel && (
                <Typography sx={{ fontSize: 14, color: AppColors.textPrimary, fontWeight: 600, mt: 1 }}>
                  お支払いの開始は{billingStartLabel}からです
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {groupedInvoices.map(group => (
                <Box key={group.key}>
                  {/* 月見出し */}
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: AppColors.textSecondary, mb: 1 }}>
                    {group.label}
                  </Typography>
                  {/* 月の請求書 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {group.items.map((invoice) => (
                      <Box
                        key={invoice.invoiceId}
                        sx={{
                          p: 2,
                          bgcolor: AppColors.surface,
                          borderRadius: 2,
                          border: `1px solid ${AppColors.border}`,
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'black' }}>
                            {invoice.number || invoice.invoiceId}
                          </Typography>
                          {invoice.amountDue === 0 ? (
                            <Chip
                              label={'トライアル（請求なし）'}
                              size="small"
                              sx={{ bgcolor: '#9e9e9e', color: 'white', fontSize: 12 }}
                            />
                          ) : (
                            <Chip
                              label={invoice.status === 'paid' ? '支払済' : invoice.status === 'open' ? '未払い' : invoice.status}
                              size="small"
                              sx={{
                                bgcolor: invoice.status === 'paid' ? '#4caf50' : invoice.status === 'open' ? '#ff9800' : '#9e9e9e',
                                color: 'white',
                                fontSize: 12,
                              }}
                            />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ fontSize: 14, color: AppColors.textSecondary }}>
                            金額:
                          </Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'black' }}>
                            ¥{invoice.amountDue.toLocaleString()}
                          </Typography>
                        </Box>
                        {/* 期限は銀行振込 かつ トライアルでない場合のみ表示 */}
                        {companyData?.paymentMethod === 'bank_transfer' && invoice.dueDate && invoice.amountDue > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography sx={{ fontSize: 14, color: AppColors.textSecondary }}>
                              期限:
                            </Typography>
                            <Typography sx={{ fontSize: 14, color: 'black' }}>
                              {invoice.dueDate.toLocaleDateString('ja-JP')}
                            </Typography>
                          </Box>
                        )}
                        {invoice.amountDue === 0 ? (
                          <Alert severity="info" sx={{ fontSize: 12 }}>
                            無料期間のため請求は発生しません
                            {companyData?.paymentMethod === 'bank_transfer' && (
                              <>
                                <br />
                                トライアル中は請求額は0円です。課金開始は翌月1日です。
                              </>
                            )}
                          </Alert>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              href={invoice.hostedInvoiceUrl}
                              target="_blank"
                              rel="noopener"
                              sx={{
                                borderColor: AppColors.primary,
                                color: AppColors.primary,
                                '&:hover': {
                                  borderColor: AppColors.primaryDark,
                                  bgcolor: AppColors.primaryLighter,
                                },
                              }}
                            >
                              請求書を開く
                            </Button>
                            {invoice.invoicePdf && (
                              <Button
                                variant="text"
                                size="small"
                                href={invoice.invoicePdf}
                                target="_blank"
                                rel="noopener"
                                sx={{
                                  color: AppColors.primary,
                                  '&:hover': {
                                    bgcolor: AppColors.primaryLighter,
                                  },
                                }}
                              >
                                PDFダウンロード
                              </Button>
                            )}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
              {/* シンプルページネーション */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 1 }}>
                {currentPage > 1 && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => loadPage(Math.max(1, currentPage - 1))}
                    disabled={invoicesLoading}
                  >
                    前へ
                  </Button>
                )}
                { hasMoreInvoices && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => loadPage(currentPage + 1)}
                    disabled={invoicesLoading}
                  >
                    次へ
                  </Button>
                )}
                {currentPage > 1 && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={resetInvoicesToInitial}
                    disabled={invoicesLoading}
                  >
                    最初へ
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Card>

        {/* 振込先情報（銀行振込の契約のみ表示） */}
        {companyData.paymentMethod === 'bank_transfer' && (
        <Card
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: `1px solid ${AppColors.border}`,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AccountBalanceIcon sx={{ fontSize: 24, color: AppColors.primary, mr: 1 }} />
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: 'black',
              }}
            >
              銀行振込先情報
            </Typography>
          </Box>

          {loadingBankInstructions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={30} />
            </Box>
          ) : bankInstructions && (bankInstructions.bankName || bankInstructions.referenceCode) ? (
            <Box
              sx={{
                p: 2,
                bgcolor: AppColors.surface,
                borderRadius: 2,
                border: `1px solid ${AppColors.border}`,
              }}
            >
              <Typography sx={{ fontSize: 14, color: AppColors.textSecondary, mb: 1 }}>
                銀行名: {bankInstructions.bankName || '-'}
              </Typography>
              <Typography sx={{ fontSize: 14, color: AppColors.textSecondary, mb: 1 }}>
                支店名: {bankInstructions.branchName || '-'}
              </Typography>
              <Typography sx={{ fontSize: 14, color: AppColors.textSecondary, mb: 1 }}>
                口座番号: {bankInstructions.accountNumber || '-'}
              </Typography>
              <Typography sx={{ fontSize: 14, color: AppColors.textSecondary, mb: 1 }}>
                口座種別: {formatBankAccountType(bankInstructions.accountType) ?? '-'}
              </Typography>
              <Typography sx={{ fontSize: 14, color: AppColors.textSecondary, mb: 1 }}>
                受取人名: {bankInstructions.accountHolderName || '-'}
              </Typography>
              {bankInstructions.referenceCode && (
                <>
                  <Typography sx={{ fontSize: 14, color: AppColors.textSecondary, mb: 1.5 }}>
                    参照コード: {bankInstructions.referenceCode}
                  </Typography>
                  <Alert severity="info" sx={{ fontSize: 12 }}>
                    振込時は必ず「参照コード」を入力してください。自動で入金が紐付きます。
                  </Alert>
                </>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                p: 3,
                bgcolor: AppColors.surface,
                borderRadius: 2,
                border: `1px solid ${AppColors.border}`,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontSize: 14, color: AppColors.textSecondary }}>
                銀行振込先情報が取得できませんでした
              </Typography>
            </Box>
          )}
        </Card>
        )}

        {/* お支払いカード情報（カード決済の契約のみ表示） */}
        {companyData.paymentMethod === 'card' && (
          <Card
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: `1px solid ${AppColors.border}`,
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CreditCardIcon sx={{ fontSize: 24, color: AppColors.primary, mr: 1 }} />
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: 'black',
                }}
              >
                お支払いカード情報
              </Typography>
            </Box>

            {loadingPaymentMethod ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={30} />
              </Box>
            ) : paymentMethod ? (
              <Box>
                {/* 未払いメッセージ */}
                {hasUnpaidInvoices && (
                  <Alert 
                    severity="warning" 
                    sx={{ 
                      mb: 2,
                      bgcolor: '#fff3cd',
                      border: '1px solid #ffc107',
                      '& .MuiAlert-icon': {
                        color: '#ff9800',
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
                      未払いの請求書があります
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      カード決済の引き落としができませんでした。お支払い後、カード情報の確認または更新をお願いいたします。
                    </Typography>
                  </Alert>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography sx={{ fontSize: 14, color: AppColors.textSecondary }}>
                    カードブランド:
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'black' }}>
                    {paymentMethod.brand?.toUpperCase() || '不明'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography sx={{ fontSize: 14, color: AppColors.textSecondary }}>
                    カード番号:
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'black' }}>
                    **** **** **** {paymentMethod.last4 || '****'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography sx={{ fontSize: 14, color: AppColors.textSecondary }}>
                    有効期限:
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'black' }}>
                    {paymentMethod.expMonth?.toString().padStart(2, '0')}/{paymentMethod.expYear}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  onClick={handleUpdateCard}
                  sx={{
                    mt: 2,
                    borderColor: AppColors.primary,
                    color: AppColors.primary,
                    '&:hover': {
                      borderColor: AppColors.primaryDark,
                      bgcolor: AppColors.primaryLighter,
                    },
                  }}
                >
                  カード情報を変更
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 3,
                  bgcolor: AppColors.surface,
                  borderRadius: 2,
                  border: `1px solid ${AppColors.border}`,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontSize: 14, color: AppColors.textSecondary }}>
                  カード情報を取得できませんでした
                </Typography>
              </Box>
            )}
          </Card>
        )}
      </Box>
      </Box>

      {/* 基本情報編集ダイアログ */}
      <Dialog
        open={basicInfoDialogOpen}
        onClose={handleBasicInfoDialogClose}
        maxWidth="sm"
        fullWidth
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0, 0, 0, 0.5)' } } }}
      >
        <DialogTitle>基本情報を編集</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="会社名"
              fullWidth
              value={basicInfoForm.companyName}
              onChange={handleBasicInfoChange('companyName')}
              error={Boolean(basicInfoErrors.companyName)}
              helperText={basicInfoErrors.companyName}
              disabled={basicInfoDialogLoading}
            />
            <TextField
              label="担当者名"
              fullWidth
              value={basicInfoForm.contactPerson}
              onChange={handleBasicInfoChange('contactPerson')}
              error={Boolean(basicInfoErrors.contactPerson)}
              helperText={basicInfoErrors.contactPerson}
              disabled={basicInfoDialogLoading}
            />
            <TextField
              label="郵便番号"
              fullWidth
              value={basicInfoForm.postalCode}
              onChange={handleBasicInfoChange('postalCode')}
              error={Boolean(basicInfoErrors.postalCode)}
              helperText={basicInfoErrors.postalCode}
              disabled={basicInfoDialogLoading}
            />
            <TextField
              label="住所"
              fullWidth
              value={basicInfoForm.address}
              onChange={handleBasicInfoChange('address')}
              error={Boolean(basicInfoErrors.address)}
              helperText={basicInfoErrors.address}
              disabled={basicInfoDialogLoading}
              multiline
              minRows={2}
            />
            <TextField
              label="電話番号"
              fullWidth
              value={basicInfoForm.phoneNumber}
              onChange={handleBasicInfoChange('phoneNumber')}
              error={Boolean(basicInfoErrors.phoneNumber)}
              helperText={basicInfoErrors.phoneNumber || 'ハイフンありでも入力できます'}
              disabled={basicInfoDialogLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBasicInfoDialogClose} disabled={basicInfoDialogLoading}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleBasicInfoSave}
            disabled={basicInfoDialogLoading}
            sx={{
              bgcolor: AppColors.primary,
              color: 'white',
              '&:hover': { bgcolor: AppColors.primary, opacity: 0.9 },
            }}
          >
            {basicInfoDialogLoading ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* メールアドレス編集ダイアログ */}
      <Dialog 
        open={emailDialogOpen} 
        onClose={handleEmailDialogClose} 
        maxWidth="sm" 
        fullWidth
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0, 0, 0, 0.5)' } } }}
      >
        <DialogTitle>メールアドレス変更</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="システム管理者メールアドレス"
            placeholder="contact@example.com"
            value={emailDialogValue}
            onChange={(e) => setEmailDialogValue(e.target.value)}
            disabled={emailDialogLoading}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: AppColors.primary,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: AppColors.primary,
                  borderWidth: 2,
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEmailDialogClose} disabled={emailDialogLoading}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleEmailDialogSave}
            disabled={emailDialogLoading}
            sx={{
              bgcolor: AppColors.primary,
              color: 'white',
              '&:hover': { bgcolor: AppColors.primary, opacity: 0.9 },
            }}
          >
            {emailDialogLoading ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* プラン選択ダイアログ */}
      <Dialog 
        open={planDialogOpen} 
        onClose={() => setPlanDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0, 0, 0, 0.5)' } } }}
      >
        <DialogTitle>プラン選択</DialogTitle>
        <DialogContent>
          {/* 
            将来的に「プラン50〜プラン500」のように複数プランを
            管理画面から直接選択できる UI を復活させる場合のために、
            以前のプラン定義をコメントとして残しています。

            以前の実装イメージ：

            {[
              { name: 'プラン50',  description: '上限端末数: ~50',  price: '¥12,000/月(税抜)' },
              { name: 'プラン100', description: '上限端末数: ~100', price: '¥15,000/月(税抜)' },
              { name: 'プラン200', description: '上限端末数: ~200', price: '¥24,000/月(税抜)' },
              { name: 'プラン300', description: '上限端末数: ~300', price: '¥30,000/月(税抜)' },
              { name: 'プラン400', description: '上限端末数: ~400', price: '¥33,000/月(税抜)' },
              { name: 'プラン500', description: '上限端末数: ~500', price: '¥36,000/月(税抜)' },
            ].map((plan) => (
              <Box key={plan.name} ...>
                // プラン名・説明・料金の表示と選択ハンドリング
              </Box>
            ))}
          */}
          {/* 現状は「プラン50」のみを選択可能とするシンプルな枠 */}
          <Box
            sx={{
              p: 2,
              border: `1px solid ${AppColors.border}`,
              borderRadius: 2,
              bgcolor: AppColors.surface,
            }}
          >
            <Typography
              sx={{
                color: AppColors.textPrimary,
                lineHeight: 1.8,
                fontSize: 14,
                whiteSpace: 'pre-line',
              }}
            >
              リアボイスは、
              お互いの顔と名前が自然に思い浮かぶ
              10〜50名程度の組織で、
              最も効果を発揮するよう設計されています。
              小さな気づきや感謝、
              面と向かっては言いづらい想いを、
              安心して届けられる距離感。
              まずはこの規模感で、
              組織の「空気の変化」を感じてみてください。
            </Typography>
            <Typography
              sx={{
                mt: 2,
                fontSize: 14,
                color: AppColors.textSecondary,
              }}
            >
              料金　¥12,000/月（税抜）（税込¥13,200）
            </Typography>
            <Box component="ul" sx={{ mt: 1.5, mb: 0, pl: 2.5, fontSize: 12, color: AppColors.textTertiary, lineHeight: 1.7 }}>
              <li>お申し込み月は無料でご利用いただけます。</li>
              <li>課金は、利用開始後の翌月1日から発生し、以降は月初（毎月1日）にその月分が課金されます。</li>
              <li>無料期間中に解約した場合、料金は発生しません。</li>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialogOpen(false)}>キャンセル</Button>
          {companyData?.currentPlan !== 'プラン50' && (
            <Button 
              onClick={() => {
                handlePlanSave('プラン50');
                setPlanDialogOpen(false);
              }}
              variant="contained"
              sx={{ 
                bgcolor: AppColors.primary,
                color: 'white',
                '&:hover': {
                  bgcolor: AppColors.primary,
                  opacity: 0.9,
                },
              }}
            >
              変更
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
        </Box>

    </ResponsiveAdminLayout>
  );
};
